// src/components/sampleorders/DeliveryDatePicker.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const JS_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDate = (d) => {
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DeliveryDatePicker = ({
  value,
  onChange,
  deliveryDays,
  leadDays = 0,
  hideLabel = false, // Optional prop to hide label
}) => {
  const [open, setOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState("down");
  const wrapperRef = useRef(null);

  // Default Mon–Fri if nothing set
  const normalizedDeliveryDays = useMemo(() => {
    const base = {
      mon: true,
      tue: true,
      wed: true,
      thu: true,
      fri: true,
      sat: false,
      sun: false,
    };
    return {
      ...base,
      ...(deliveryDays || {}),
    };
  }, [deliveryDays]);

  const minDate = useMemo(() => {
    const d = getToday();
    if (leadDays > 0) {
      d.setDate(d.getDate() + leadDays);
    }
    return d;
  }, [leadDays]);

  // Initial month/year shown
  const initialDate = useMemo(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      if (!Number.isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    return minDate;
  }, [value, minDate]);

  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth()); // 0–11

  const handleMonthChange = (direction) => {
    setCurrentMonth((prev) => {
      let newMonth = prev + direction;
      let newYear = currentYear;

      if (newMonth < 0) {
        newMonth = 11;
        newYear = currentYear - 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear = currentYear + 1;
      }

      setCurrentYear(newYear);
      return newMonth;
    });
  };

  // Build calendar cells
  const cells = useMemo(() => {
    const firstOfMonth = new Date(currentYear, currentMonth, 1);
    const startWeekday = firstOfMonth.getDay(); // 0 = Sun
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    const tmpCells = [];

    // Leading empty cells
    for (let i = 0; i < startWeekday; i++) {
      tmpCells.push(null);
    }

    // Real days
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(currentYear, currentMonth, day);
      d.setHours(0, 0, 0, 0);

      const dayKey = JS_DAY_KEYS[d.getDay()];
      const dateStr = formatDate(d);

      const isBeforeMin = d.getTime() < minDate.getTime();
      const isAllowedWeekday = !!normalizedDeliveryDays[dayKey];

      const disabled = isBeforeMin || !isAllowedWeekday;

      tmpCells.push({
        date: d,
        dateStr,
        disabled,
      });
    }

    // Pad to full weeks (6 rows * 7 days = 42 cells) if needed
    while (tmpCells.length < 42) {
      tmpCells.push(null);
    }

    return tmpCells;
  }, [currentYear, currentMonth, minDate, normalizedDeliveryDays]);

  const selectedDateStr = value || "";

  const monthLabel = useMemo(() => {
    const d = new Date(currentYear, currentMonth, 1);
    return d.toLocaleString(undefined, {
      month: "long",
      year: "numeric",
    });
  }, [currentYear, currentMonth]);

  const deliveryDaysSummary = useMemo(() => {
    const labelsMap = {
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
      sun: "Sun",
    };
    const active = Object.keys(labelsMap).filter(
      (key) => normalizedDeliveryDays[key]
    );
    if (active.length === 7) return "Every day";
    if (active.length === 0) return "No days selected";
    return active.map((k) => labelsMap[k]).join(", ");
  }, [normalizedDeliveryDays]);

  const handleDayClick = (cell) => {
    if (!cell || cell.disabled) return;
    onChange(cell.dateStr);
    setOpen(false); // close after selecting
  };

  const formattedSelected = selectedDateStr
    ? new Date(selectedDateStr + "T00:00:00").toLocaleDateString()
    : "";

  useEffect(() => {
    if (!open) return undefined;

    const updatePlacement = () => {
      const rect = wrapperRef.current?.getBoundingClientRect?.();
      if (!rect || typeof window === "undefined") return;

      const estimatedHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenDirection(spaceBelow < estimatedHeight && spaceAbove > spaceBelow ? "up" : "down");
    };

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative overflow-visible">
      {/* "Input" button to toggle calendar */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]"
      >
        <span className={formattedSelected ? "text-gray-900" : "text-gray-400"}>
          {formattedSelected || "Select delivery date"}
        </span>
        <Calendar className="w-4 h-4 text-gray-400" />
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div
          className={`absolute left-0 z-50 w-[min(240px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-3 shadow-xl ${
            openDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => handleMonthChange(-1)}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-medium text-gray-800">
              {monthLabel}
            </div>
            <button
              type="button"
              onClick={() => handleMonthChange(1)}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Weekday labels */}
          <div className="grid grid-cols-7 gap-1 text-[11px] text-gray-500 mb-1">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="text-center">
                {label}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1 text-xs">
            {cells.map((cell, index) => {
              if (!cell) {
                return <div key={index} className="h-7" />;
              }

              const isSelected =
                !cell.disabled && cell.dateStr === selectedDateStr;

              let className =
                "w-7 h-7 flex items-center justify-center rounded-full ";

              if (cell.disabled) {
                className +=
                  "bg-gray-50 text-gray-300 cursor-not-allowed";
              } else if (isSelected) {
                className +=
                  "bg-blue-600 text-white cursor-pointer hover:bg-blue-600";
              } else {
                className +=
                  "text-gray-700 cursor-pointer hover:bg-blue-50";
              }

              return (
                <button
                  type="button"
                  key={cell.dateStr}
                  onClick={() => handleDayClick(cell)}
                  className={className}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-2 space-y-0.5">
            <p className="text-[11px] text-gray-500">
              Available days: {deliveryDaysSummary}
              {leadDays > 0 &&
                ` • Minimum ${leadDays} day${
                  leadDays === 1 ? "" : "s"
                } notice`}
            </p>
            {formattedSelected && (
              <p className="text-[11px] text-gray-600">
                Selected: {formattedSelected}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDatePicker;
